import { ResponsiveTable } from '../components/ResponsiveTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { ShopColumnHeader, ShopColumnCell } from '../components/ShopListColumn';
import { RootState } from '../store';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { useVisibleSelection } from '../hooks/useVisibleSelection';

type ContractStatus = 'DRAFT' | 'PENDING' | 'SIGNED' | 'EXPIRED' | 'CANCELLED';

type ContractRow = {
  id: string;
  clientId: string;
  actId: string | null;
  type: string;
  templateVersion: string;
  status: ContractStatus;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  client: { id: string; firstName: string | null; lastName: string | null; email: string | null };
  act: { id: string; appointmentId: string | null; shop?: { id: string; name: string } | null } | null;
  generatedDocumentCount: number;
};

const PAGE_SIZE = 25;

type ContractSortKey =
  | 'client'
  | 'type'
  | 'status'
  | 'signedAt'
  | 'generatedDocumentCount'
  | 'appointment'
  | 'createdAt'
  | 'templateVersion'
  | 'updatedAt'
  | 'id';

function formatDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR');
}

function statusLabel(s: ContractStatus) {
  const m: Record<ContractStatus, string> = {
    DRAFT: 'Brouillon',
    PENDING: 'En attente',
    SIGNED: 'Signé',
    EXPIRED: 'Expiré',
    CANCELLED: 'Annulé',
  };
  return m[s] || s;
}

function typeLabel(t: string) {
  return t.replace(/_/g, ' ');
}

function SortHeader(props: {
  label: string;
  sortKey: ContractSortKey;
  activeKey: ContractSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: ContractSortKey) => void;
  className?: string;
}) {
  const { label, sortKey, activeKey, sortDir, onSort, className } = props;
  const active = activeKey === sortKey;
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase ${className || ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
          )
        ) : (
          <span className="inline-flex flex-col leading-none text-gray-300" aria-hidden>
            <ArrowUp className="w-3 h-3 -mb-1" />
            <ArrowDown className="w-3 h-3" />
          </span>
        )}
      </button>
    </th>
  );
}

export function ContractsListPage() {
  const { user } = useSelector((state: RootState) => state.auth);
  const role = (user as { role?: string } | undefined)?.role;
  const isSuperAdmin = role === 'super_admin';
  const canHardDelete = role === 'super_admin';

  const { showToast, ToastComponent } = useToast();
  const [rows, setRows] = useState<ContractRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContractStatus | ''>('');
  const [type, setType] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [sortKey, setSortKey] = useState<ContractSortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const {
    selectedIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  const [hardModal, setHardModal] = useState<{ open: boolean; ids: string[] }>({ open: false, ids: [] });
  const [hardLoading, setHardLoading] = useState(false);
  const [archiveSignedModal, setArchiveSignedModal] = useState<{ open: boolean; row: ContractRow | null }>({
    open: false,
    row: null,
  });
  const [archiveSoftModal, setArchiveSoftModal] = useState<{ open: boolean; row: ContractRow | null }>({
    open: false,
    row: null,
  });
  const [bulkArchiveModal, setBulkArchiveModal] = useState(false);
  const [bulkArchiveForceSigned, setBulkArchiveForceSigned] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const buildQs = useCallback(
    (p: number) => {
      const qs = new URLSearchParams({
        page: String(p),
        limit: String(PAGE_SIZE),
        sortKey,
        sortDir,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status ? { status } : {}),
        ...(type.trim() ? { type: type.trim() } : {}),
        ...(includeDeleted ? { includeDeleted: 'true' } : {}),
      });
      return qs;
    },
    [search, status, type, includeDeleted, sortKey, sortDir],
  );

  const load = useCallback(
    async (pageOverride?: number) => {
      let p = pageOverride ?? page;
      try {
        setLoading(true);
        setError(null);
        let res = await apiClient(`/api/admin/contracts?${buildQs(p)}`, { method: 'GET' }, true);
        let data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.message || 'Impossible de charger les contrats');
        }
        const inner = data.data;
        const totalCount = typeof inner?.total === 'number' ? inner.total : 0;
        const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
        if (p > maxPage) {
          p = maxPage;
          res = await apiClient(`/api/admin/contracts?${buildQs(p)}`, { method: 'GET' }, true);
          data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.message || 'Impossible de charger les contrats');
        }
        setRows(data.data?.contracts || []);
        setTotal(typeof data.data?.total === 'number' ? data.data.total : 0);
        setPage(p);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Erreur');
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [page, buildQs],
  );

  useEffect(() => {
    if (!canLoad) return;
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, currentShopId, sortKey, sortDir]);

  const handleSort = (key: ContractSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const softArchive = async (row: ContractRow, forceSigned: boolean) => {
    try {
      setArchiveLoading(true);
      const qs = forceSigned ? '?forceSigned=true' : '';
      const res = await apiClient(`/api/admin/contracts/${encodeURIComponent(row.id)}${qs}`, { method: 'DELETE' }, true);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Échec');
      showToast(data?.message || 'Contrat archivé', 'success');
      setArchiveSignedModal({ open: false, row: null });
      setArchiveSoftModal({ open: false, row: null });
      await load(page);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Erreur', 'error', 7000);
    } finally {
      setArchiveLoading(false);
    }
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const bulkArchiveNeedsForceSigned = useMemo(
    () => selectedRows.some((r) => r.status === 'SIGNED' || !!r.signedAt),
    [selectedRows],
  );

  const confirmBulkArchive = async () => {
    if (selectedCount === 0) return;
    if (bulkArchiveNeedsForceSigned && !isSuperAdmin) {
      showToast('La sélection contient des contrats signés : seul un super-admin peut les archiver en lot.', 'error', 8000);
      setBulkArchiveModal(false);
      return;
    }
    const forceSigned = bulkArchiveNeedsForceSigned && isSuperAdmin && bulkArchiveForceSigned;
    if (bulkArchiveNeedsForceSigned && isSuperAdmin && !bulkArchiveForceSigned) {
      showToast('Cochez « Inclure les contrats signés » pour archiver des contrats déjà signés.', 'error', 6000);
      return;
    }
    try {
      setArchiveLoading(true);
      const res = await apiClient(
        '/api/admin/contracts/bulk-archive',
        {
          method: 'POST',
          body: JSON.stringify({
            ids: selectedRows.map((r) => r.id),
            ...(forceSigned ? { forceSigned: true } : {}),
          }),
        },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Échec');
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const archived = (data.data?.archived as string[] | undefined)?.length ?? 0;
      const skipped = (data.data?.skipped as string[] | undefined)?.length ?? 0;
      if (failed?.length) {
        showToast(`${data.message || 'Archivage partiel'} (${failed.length} échec(s))`, 'warning', 8000);
      } else {
        showToast(
          `${archived} archivé(s)${skipped ? `, ${skipped} déjà archivé(s)` : ''}`,
          'success',
        );
      }
      setBulkArchiveModal(false);
      setBulkArchiveForceSigned(false);
      clearSelection();
      await load(page);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Erreur', 'error', 7000);
    } finally {
      setArchiveLoading(false);
    }
  };

  const hardDeleteOne = async () => {
    if (hardModal.ids.length !== 1) return;
    const id = hardModal.ids[0];
    try {
      setHardLoading(true);
      const res = await apiClient(
        `/api/admin/contracts/${encodeURIComponent(id)}?hardDelete=true`,
        { method: 'DELETE' },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Échec');
      showToast(data?.message || 'Supprimé définitivement', 'success');
      setHardModal({ open: false, ids: [] });
      await load(page);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Erreur', 'error', 7000);
    } finally {
      setHardLoading(false);
    }
  };

  const confirmBulkHardDelete = async () => {
    if (hardModal.ids.length === 0) return;
    try {
      setHardLoading(true);
      const res = await apiClient(
        '/api/admin/contracts/bulk-delete-permanent',
        {
          method: 'POST',
          body: JSON.stringify({ ids: hardModal.ids }),
        },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Échec');
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const deleted = (data.data?.deleted as string[] | undefined)?.length ?? 0;
      if (failed?.length) {
        showToast(`${data.message || 'Suppression partielle'} (${failed.length} échec(s))`, 'warning', 8000);
      } else {
        showToast(`${deleted} contrat(s) supprimé(s) définitivement`, 'success');
      }
      setHardModal({ open: false, ids: [] });
      clearSelection();
      await load(page);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Erreur', 'error', 7000);
    } finally {
      setHardLoading(false);
    }
  };

  const openArchive = (row: ContractRow) => {
    const signed = row.status === 'SIGNED' || !!row.signedAt;
    if (signed && isSuperAdmin) {
      setArchiveSignedModal({ open: true, row });
      return;
    }
    if (signed && !isSuperAdmin) {
      showToast('Contrat signé : seul un super-admin peut archiver ou supprimer.', 'error', 7000);
      return;
    }
    setArchiveSoftModal({ open: true, row });
  };

  const openBulkArchive = () => {
    if (selectedCount === 0) return;
    if (bulkArchiveNeedsForceSigned && !isSuperAdmin) {
      showToast('La sélection contient des contrats signés : seul un super-admin peut les archiver.', 'error', 8000);
      return;
    }
    setBulkArchiveForceSigned(false);
    setBulkArchiveModal(true);
  };

  const openBulkHard = () => {
    if (selectedCount === 0 || !canHardDelete) return;
    setHardModal({ open: true, ids: selectedRows.map((r) => r.id) });
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="p-6">
      <ToastComponent />

      <div className="dt-list-toolbar mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary-600" />
            Contrats
          </h1>
          <p className="text-textSecondary mt-1 text-sm max-w-2xl">
            Fiches <code className="text-xs bg-gray-100 px-1 rounded">contracts</code> (données juridiques). Les PDF
            générés sont dans <code className="text-xs bg-gray-100 px-1 rounded">generated_documents</code> avec{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">entityType=CONTRACT</code> et le même id.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load(page)}
          disabled={loading}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {selectedCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-medium text-primary-900">
            {selectedCount} sélectionné{selectedCount !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            disabled={loading || archiveLoading}
            onClick={openBulkArchive}
          >
            Archiver la sélection
          </button>
          {canHardDelete && (
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-red-800 text-white hover:bg-red-900 disabled:opacity-50 inline-flex items-center gap-1"
              disabled={loading || hardLoading}
              onClick={openBulkHard}
            >
              <Trash2 className="w-4 h-4" />
              Supprimer définitivement
            </button>
          )}
          <button type="button" className="text-sm text-gray-600 hover:text-gray-900 underline" onClick={clearSelection}>
            Vider la sélection
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6 space-y-4">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input-field pl-10"
              placeholder="Client (nom, e-mail)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Statut</label>
            <select
              className="input-field"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContractStatus | '')}
            >
              <option value="">Tous</option>
              <option value="DRAFT">Brouillon</option>
              <option value="PENDING">En attente</option>
              <option value="SIGNED">Signé</option>
              <option value="EXPIRED">Expiré</option>
              <option value="CANCELLED">Annulé</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-gray-600 mb-1">Type (code Prisma)</label>
            <input
              className="input-field font-mono text-sm"
              placeholder="ex. PIERCING_CONSENT"
              value={type}
              onChange={(e) => setType(e.target.value)}
            />
          </div>
          <label className="md:col-span-3 flex items-center gap-2 cursor-pointer text-sm text-gray-700 pb-1">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="rounded border-gray-300"
            />
            Afficher les contrats archivés
          </label>
        </div>
        <button type="button" className="btn-primary" disabled={loading} onClick={() => void load(1)}>
          Filtrer
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={allVisibleSelected}
                    disabled={loading || rows.length === 0}
                    onChange={toggleSelectAllVisible}
                    aria-label="Tout sélectionner sur la page"
                  />
                </th>
                <SortHeader
                  label="Client"
                  sortKey="client"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <ShopColumnHeader visible={isAllShopsMode} />
                <SortHeader label="Type" sortKey="type" activeKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader
                  label="Statut"
                  sortKey="status"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Signé"
                  sortKey="signedAt"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Fichiers"
                  sortKey="generatedDocumentCount"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Rendez-vous"
                  sortKey="appointment"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Modèle"
                  sortKey="templateVersion"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Créé"
                  sortKey="createdAt"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Modifié"
                  sortKey="updatedAt"
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader label="Id" sortKey="id" activeKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary-600" />
                    Chargement…
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const archived = !!row.deletedAt;
                  return (
                    <tr key={row.id} className={archived ? 'bg-gray-50 opacity-80' : ''}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelect(row.id)}
                          disabled={loading}
                          aria-label={`Sélectionner ${row.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {row.client?.firstName || ''} {row.client?.lastName || ''}
                        </div>
                        <div className="text-gray-500 text-xs">{row.client?.email || '—'}</div>
                      </td>
                      <ShopColumnCell visible={isAllShopsMode} shopName={row.act?.shop?.name} />
                      <td className="px-4 py-3 text-sm text-gray-700">{typeLabel(row.type)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            row.status === 'SIGNED'
                              ? 'bg-green-100 text-green-800'
                              : row.status === 'CANCELLED'
                                ? 'bg-gray-200 text-gray-700'
                                : 'bg-amber-50 text-amber-800'
                          }`}
                        >
                          {statusLabel(row.status)}
                          {archived && ' · archivé'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(row.signedAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.generatedDocumentCount}</td>
                      <td className="px-4 py-3 text-sm">
                        {row.act?.appointmentId ? (
                          <Link
                            to={`/appointments/${row.act.appointmentId}`}
                            className="text-primary-600 hover:underline inline-flex items-center gap-1"
                          >
                            Voir RDV
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">{row.templateVersion}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row.updatedAt)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-[11px] text-gray-500 max-w-[120px] truncate">
                        {row.id}
                      </td>
                      <td className="px-4 py-3 text-right text-sm whitespace-nowrap space-x-2">
                        {!archived && (
                          <button
                            type="button"
                            onClick={() => openArchive(row)}
                            className="text-amber-700 hover:text-amber-900 font-medium"
                            title="Archiver (suppression logique)"
                          >
                            Archiver
                          </button>
                        )}
                        {canHardDelete && (
                          <button
                            type="button"
                            onClick={() => setHardModal({ open: true, ids: [row.id] })}
                            className="text-red-700 hover:text-red-900 font-medium inline-flex items-center gap-1"
                            title="Supprimer définitivement la fiche et les fichiers générés liés"
                          >
                            <Trash2 className="w-4 h-4" />
                            Définitif
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {total > 0 && (
          <div className="dt-list-toolbar flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 bg-gray-50">
            <p className="text-sm text-gray-600">
              {total} contrat{total !== 1 ? 's' : ''} · page {page} / {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-sm"
                disabled={loading || page <= 1}
                onClick={() => void load(page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
                Précédent
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-sm"
                disabled={loading || page >= totalPages}
                onClick={() => void load(page + 1)}
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucun contrat ne correspond aux critères.</div>
        )}
      </div>

      <ConfirmDefinitiveDeleteModal
        isOpen={hardModal.open}
        onClose={() => (hardLoading ? null : setHardModal({ open: false, ids: [] }))}
        onConfirm={() => (hardModal.ids.length === 1 ? void hardDeleteOne() : void confirmBulkHardDelete())}
        entityLabel="contrat(s)"
        count={hardModal.ids.length || 1}
        isLoading={hardLoading}
      />

      <ConfirmModal
        isOpen={archiveSoftModal.open}
        onClose={() => (archiveLoading ? undefined : setArchiveSoftModal({ open: false, row: null }))}
        onConfirm={() => archiveSoftModal.row && void softArchive(archiveSoftModal.row, false)}
        title="Archiver le contrat"
        message="Le contrat sera masqué des listes par défaut (suppression logique) et son statut passera à Annulé."
        confirmLabel="Archiver"
        confirmVariant="warning"
        isLoading={archiveLoading}
      />

      <ConfirmModal
        isOpen={archiveSignedModal.open}
        onClose={() => (archiveLoading ? undefined : setArchiveSignedModal({ open: false, row: null }))}
        onConfirm={() => archiveSignedModal.row && void softArchive(archiveSignedModal.row, true)}
        title="Archiver un contrat signé"
        message="Vous allez archiver (suppression logique) un contrat déjà signé. La fiche restera en base avec une date d’archivage. Réservé au super-admin."
        confirmLabel="Archiver"
        confirmVariant="warning"
        isLoading={archiveLoading}
      />

      <ConfirmModal
        isOpen={bulkArchiveModal}
        onClose={() => (archiveLoading ? undefined : setBulkArchiveModal(false))}
        onConfirm={() => void confirmBulkArchive()}
        title="Archiver la sélection"
        message={
          bulkArchiveNeedsForceSigned && isSuperAdmin
            ? `Archiver ${selectedCount} contrat(s). Les contrats signés nécessitent l’option ci-dessous.`
            : `Archiver ${selectedCount} contrat(s) (suppression logique).`
        }
        confirmLabel="Archiver"
        confirmVariant="warning"
        isLoading={archiveLoading}
      >
        {bulkArchiveNeedsForceSigned && isSuperAdmin && (
          <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1 rounded border-gray-300"
              checked={bulkArchiveForceSigned}
              onChange={(e) => setBulkArchiveForceSigned(e.target.checked)}
            />
            <span>Inclure les contrats déjà signés (super-admin)</span>
          </label>
        )}
      </ConfirmModal>
    </div>
  );
}
